import { Metadata } from "@dropbox/dash-component-library";
import { useMemo } from "react";

import { Text } from "@dropbox/dig-components/typography";
import { getSourceRowParts } from "@mirage/mosaics/Chat/components/compose-sources/ComposeSourceRow";
import type {
  Conversation,
  DocumentInfo,
  UserMessageOptions,
} from "@mirage/service-conversation/types";
import { ProgressUpdateStep } from "@mirage/service-conversation/types";
import { convertDocumentInfoToChatSource } from "@mirage/service-conversation/utils/convertDocumentInfoToChatSource";
import { useTheme } from "@mirage/service-settings/theming/useTheme";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";
import i18n from "@mirage/translations";
import classNames from "classnames";
import styles from "./ProgressAnimationComponent.module.css";

interface ProgressAnimationComponentProps {
  isWaitingForResponse: boolean;
  progressStep?: ProgressUpdateStep;
  userMessageOptions?: UserMessageOptions;
  sources?: DocumentInfo[];
  conversation: Conversation;
}

export function ProgressAnimationComponent({
  isWaitingForResponse,
  progressStep,
  userMessageOptions: _userMessageOptions,
  sources = [],
  conversation,
}: ProgressAnimationComponentProps) {
  const isDarkMode = useTheme();
  const sourcesRowParts = useMemo(() => {
    const parts = [];

    const chatSources = convertDocumentInfoToChatSource(sources);

    for (const chatSource of chatSources) {
      const sourcesRowPart = getSourceRowParts(chatSource, {
        icon: {
          hasBackground: false,
          size: "small",
        },
      });

      if (sourcesRowPart) {
        parts.push(sourcesRowPart);
      }
    }
    return parts;
  }, [sources]);

  const uniqueSourceIcons = useMemo(() => {
    return Array.from(
      new Map(
        sourcesRowParts.map((item) => [
          item.mirageResult.connectorInfo.connectorId,
          item,
        ]),
      ).values(),
    ).slice(0, 3); // Limit to 3 icons like in the Figma
  }, [sourcesRowParts]);
  const stepText =
    isWaitingForResponse && conversation?.progressUpdates?.length === 0
      ? i18n.t("conversation_step_label_searching")
      : getStepLabel(
          progressStep || ProgressUpdateStep.STEP_INTENT_DETECTION,
          conversation,
        );

  if (!stepText) {
    return null;
  }

  return (
    <div className={styles.ProgressAnimationContainer}>
      {stepText === i18n.t("conversation_step_label_reading_files") &&
        uniqueSourceIcons.length > 0 && (
          <div className={styles.ProgressAnimationIcons}>
            {uniqueSourceIcons.map((sourcesRowPart, _i) => (
              <div
                key={sourcesRowPart.mirageResult.connectorInfo.connectorId}
                className={styles.ProgressAnimationIcon}
              >
                <Metadata.Item>{sourcesRowPart.icon}</Metadata.Item>
              </div>
            ))}
          </div>
        )}
      <Text
        key={stepText}
        className={classNames(styles.ProgressAnimationText, {
          [styles.ProgressAnimationTextDark]: isDarkMode,
          [styles.ProgressAnimationTextEmbed]: isEmbedPage(),
        })}
      >
        {stepText}
      </Text>
    </div>
  );
}

const getStepLabel = (
  update: ProgressUpdateStep,
  conversation: Conversation,
) => {
  switch (update) {
    case ProgressUpdateStep.STEP_INTENT_DETECTION:
      return i18n.t("conversation_step_label_searching");
    case ProgressUpdateStep.STEP_SEARCH:
    case ProgressUpdateStep.STEP_CONTEXT_SELECTION:
      return i18n.t("conversation_step_label_reading_files");
    case ProgressUpdateStep.STEP_RESPONSE_GENERATION_FROM_LLM:
    case ProgressUpdateStep.STEP_RESPONSE_GENERATION_FROM_AGENT: {
      // Show "Drafting" if there's a markdown draft
      if (conversation?.markdownDraft) {
        return i18n.t("conversation_step_label_drafting");
      }

      // Show "Reading files" if the assistant message text is still empty.
      // This is caused by the fact that the progress update switches to streaming
      // even though the text is still empty.
      const lastMessage =
        conversation.messages[conversation.messages.length - 1];
      const isAssistantMessageNotEmpty =
        lastMessage?.messageType?.case === "assistantMessage" &&
        lastMessage.messageType?.value?.text?.length > 0;
      return isAssistantMessageNotEmpty
        ? undefined
        : i18n.t("conversation_step_label_reading_files");
    }
    case ProgressUpdateStep.STEP_SUGGESTED_QUERIES_GENERATION:
      return undefined; // hide this step
    default:
      return i18n.t("conversation_step_label_searching");
  }
};
